import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { query, type NivellEscalatDto, type TipusAvisDto } from './activacions';
import { ApiError, apiRequest, apiRequestAmbEtag } from './client';
import { useAmbEtag } from './etag';
import type { AreaDto, DepartamentDto, ServeiDto } from './planificacio';
import type { Ambit, Canal, Pagina, TipusAcces } from './types';

export type TipusZona = 'CENTRE' | 'PLANTA' | 'BOTIGA' | 'OFICINA' | 'ALTRE';
export type OrigenUsuariInformar = 'MANUAL' | 'ENTRA' | 'EXCEL';
export type EstatDocument = 'ESBORRANY' | 'PUBLICAT' | 'RETIRAT' | 'BLOQUEJAT';
export type EstatEscaneig = 'PENDENT' | 'NET' | 'INFECTAT' | 'ERROR';
export type AmbitDocument = 'SERVEI' | 'AREA' | 'DEPARTAMENT';
export type Plataforma = 'IOS' | 'ANDROID';
export type TipusDesti =
  | 'TECNIC_GUARDIA_TITULAR'
  | 'TECNIC_GUARDIA_SUBSTITUT'
  | 'TECNIC_GUARDIA_DEPARTAMENT'
  | 'TECNIC_GUARDIA_ALTRES_AREES'
  | 'EXPERT_TITULAR'
  | 'EXPERT_SUBSTITUT'
  | 'USUARI'
  | 'GRUP';

export const TIPUS_DESTI: TipusDesti[] = [
  'TECNIC_GUARDIA_TITULAR',
  'TECNIC_GUARDIA_SUBSTITUT',
  'TECNIC_GUARDIA_DEPARTAMENT',
  'TECNIC_GUARDIA_ALTRES_AREES',
  'EXPERT_TITULAR',
  'EXPERT_SUBSTITUT',
  'USUARI',
  'GRUP',
];
export const TIPUS_ACCES: TipusAcces[] = ['CONSULTAR', 'ALTA', 'MODIFICAR', 'ELIMINAR'];
export const TIPUS_ZONA: TipusZona[] = ['CENTRE', 'PLANTA', 'BOTIGA', 'OFICINA', 'ALTRE'];

export interface UsuariAdminDto {
  id: number;
  entraOid?: string | null;
  upn?: string | null;
  nom: string;
  cognoms?: string | null;
  email?: string | null;
  telefon?: string | null;
  departamentId?: number | null;
  carrec?: string | null;
  idioma?: string | null;
  actiu: boolean;
  ultimSync?: string | null;
  version?: number;
}

export interface UsuariRolDto {
  id?: number | null;
  usuariId: number;
  rolId: number;
  rolCodi?: string | null;
  rolNomCa?: string | null;
  rolNomEs?: string | null;
  ambit: Ambit;
  ambitId?: number | null;
  ambitNom?: string | null;
  desde?: string | null;
  fins?: string | null;
}

export interface CanalVerificatDto {
  id: number;
  usuariId: number;
  canal: string;
  valor: string;
  verificat: boolean;
  verificatAt?: string | null;
}

export interface DispositiuDto {
  id: number;
  usuariId: number;
  plataforma: string;
  model?: string | null;
  appVersio?: string | null;
  alertesCritiques: boolean;
  biometria: boolean;
  ultimAcces?: string | null;
  actiu: boolean;
}

export interface UsuariDetallDto {
  usuari: UsuariAdminDto;
  departamentNom?: string | null;
  rols: UsuariRolDto[];
  canalsVerificats: CanalVerificatDto[];
  dispositius: DispositiuDto[];
}

export interface ResultatSyncDto {
  creats: number;
  actualitzats: number;
  desactivats: number;
}

export interface RolDto {
  id?: number | null;
  codi: string;
  nomCa: string;
  nomEs: string;
  descripcio?: string | null;
  sistema: boolean;
  version?: number;
}

export interface PermisDto {
  id?: number | null;
  recurs: string;
  tipusAcces: TipusAcces;
  descripcioCa?: string | null;
  descripcioEs?: string | null;
}

export interface ServeiAdminDto extends ServeiDto {
  descripcio?: string | null;
  plantillaId?: number | null;
  circuitEscalatId?: number | null;
  canalsDefecte?: Canal[];
  procedimentAlternatiu?: string | null;
  actiu?: boolean;
  version?: number;
}

export interface ZonaAdminDto {
  id?: number | null;
  codi: string;
  nom: string;
  tipus: TipusZona;
  adreca?: string | null;
  pareId?: number | null;
  actiu: boolean;
  version?: number;
}

export interface UsuariInformarDto {
  id?: number | null;
  serveiId: number;
  usuariId?: number | null;
  nom: string;
  email?: string | null;
  telefon?: string | null;
  origen: OrigenUsuariInformar;
  importLotId?: number | null;
  canals: Canal[];
  actiu: boolean;
}

export interface MapeigDto {
  nom: string;
  email: string;
  telefon?: string | null;
  canals?: string | null;
}

export interface ImportErrorDto {
  fila: number;
  camp: string;
  codi: string;
  missatge?: string | null;
}

export interface ImportResultatDto {
  total: number;
  valids: number;
  duplicats: number;
  desats: number;
  importLotId?: number | null;
  errors: ImportErrorDto[];
}

export interface TraduccioPlantillaDto {
  titol: string;
  cos: string;
  cosEmailHtml?: string | null;
  cosPush?: string | null;
  cosVeu?: string | null;
}

export interface PlantillaDto {
  id?: number | null;
  codi: string;
  tipus: string;
  variables: string[];
  actiu: boolean;
  traduccions: Record<string, TraduccioPlantillaDto>;
}

export interface PrevisualitzacioPlantillaDto {
  canal: string;
  idioma: string;
  titol: string;
  cos: string;
}

export interface TipusAvisAdminDto extends TipusAvisDto {
  actiu?: boolean;
  traduccions?: Record<string, { nom: string; descripcio?: string | null }>;
  version?: number;
}

export interface DocumentVersioDto {
  id: number;
  versio: number;
  nomFitxer: string;
  mime?: string | null;
  mida: number;
  sha256?: string | null;
  estatEscaneig: EstatEscaneig;
  detallEscaneig?: string | null;
  pujatPerId?: number | null;
  pujatPer?: string | null;
  pujatAt: string;
  comentari?: string | null;
}

export interface DocumentAdminDto {
  id: number;
  codi?: string | null;
  serveiId: number;
  servei?: string | null;
  titol: string;
  categoria?: string | null;
  ambit: AmbitDocument;
  responsableId?: number | null;
  responsable?: string | null;
  estat: EstatDocument;
  motiuEstat?: string | null;
  descarregable: boolean;
  versioVigent?: DocumentVersioDto | null;
  darreraVersio?: DocumentVersioDto | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PoliticaFitxersDto {
  serveiId?: number | null;
  propia: boolean;
  formats: string[];
  midaMaxMb: number;
  maxDocuments: number;
  updatedAt?: string | null;
  version?: number;
}

export interface AppVersioDto {
  id?: number | null;
  plataforma: Plataforma;
  versio: string;
  build?: string | null;
  minimaObligatoria: boolean;
  publicadaAt?: string | null;
  notesCa?: string | null;
  notesEs?: string | null;
  urlBotiga?: string | null;
}

export interface MissatgeAdminDto {
  id?: number | null;
  tipus: string;
  titolCa: string;
  titolEs: string;
  cosCa: string;
  cosEs: string;
  plataformes: Plataforma[];
  versioMin?: string | null;
  versioMax?: string | null;
  desde?: string | null;
  fins?: string | null;
  actiu: boolean;
  createdAt?: string | null;
}

export interface ConfiguracioDto {
  valors: Record<string, string>;
}

export interface RegistreAuditoriaDto {
  id: number;
  at: string;
  usuariId?: number | null;
  upn?: string | null;
  accio: string;
  entitat: string;
  entitatId?: string | null;
  valorPrevi?: string | null;
  valorNou?: string | null;
  resultat: string;
  codiError?: string | null;
  ip?: string | null;
}

export interface EstatPasarella {
  nom: string;
  estat: 'OK' | 'DEGRADAT' | 'KO' | 'NO_CONFIGURAT';
  ultimError?: string | null;
  enviats: number;
  fallits: number;
}

export interface EstatMonitoratge {
  cua: number;
  latenciaP50?: number | null;
  latenciaP95?: number | null;
  passarelles: EstatPasarella[];
  errors?: ErrorEnviament[];
}

export interface ErrorEnviament {
  enviamentId?: number | null;
  destinatariId?: number | null;
  canal: string;
  estat: string;
  codi?: string | null;
  detall?: string | null;
  at: string;
}

function senseAcces<T>(buit: T) {
  return (e: unknown): T => {
    if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return buit;
    throw e;
  };
}

export function useLlista<T>(clau: QueryKey, ruta: string, enabled = true) {
  return useQuery({
    queryKey: clau,
    queryFn: () => apiRequest<T[]>(ruta),
    enabled,
  });
}

/**
 * Desa una entitat: POST si és nova, PUT amb `If-Match` si ja existeix. L'ETag ha de ser el
 * de la lectura que va carregar el formulari (useEtag), mai un de llegit just abans del PUT:
 * així un canvi concurrent es detecta (412) en lloc de sobreescriure'l.
 */
export async function desa<T extends { id?: number | null }>(
  ruta: string,
  entitat: T,
  etag?: string | null,
): Promise<T> {
  if (entitat.id == null) return apiRequest<T>(ruta, { method: 'POST', json: entitat });
  return apiRequest<T>(`${ruta}/${entitat.id}`, {
    method: 'PUT',
    json: entitat,
    headers: etag ? { 'If-Match': etag } : undefined,
  });
}

export async function desaRecurs<T>(ruta: string, cos: T, etag?: string | null): Promise<T> {
  return apiRequest<T>(ruta, {
    method: 'PUT',
    json: cos,
    headers: etag ? { 'If-Match': etag } : undefined,
  });
}

/** ETag de la versió d'una entitat que l'usuari està editant (llegit en obrir el formulari). */
export function useEtag(ruta: string, id: number | null | undefined) {
  const q = useAmbEtag<unknown>(id != null ? `${ruta}/${id}` : null);
  return { etag: q.data?.etag ?? null, carregant: id != null && q.isLoading };
}

export function useDesa<T extends { id?: number | null }>(
  ruta: string,
  invalida: QueryKey,
  etag?: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (e: T) => desa<T>(ruta, e, etag),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invalida });
      void qc.invalidateQueries({ queryKey: ['etag'] });
    },
  });
}

export function useElimina(ruta: string, invalida: QueryKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiRequest<void>(`${ruta}/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: invalida }),
  });
}

export interface FiltreUsuaris {
  text?: string;
  rolId?: number | '';
  departamentId?: number | '';
  actiu?: boolean | '';
  page?: number;
}

export function useUsuarisAdmin(f: FiltreUsuaris) {
  return useQuery({
    queryKey: ['usuaris', 'admin', f],
    queryFn: () =>
      apiRequest<Pagina<UsuariAdminDto>>(`/usuaris${query({ ...f, page: f.page ?? 0, size: 50 })}`),
  });
}

export function useUsuariDetall(id: number) {
  return useQuery({
    queryKey: ['usuaris', 'detall', id],
    queryFn: () => apiRequest<UsuariDetallDto>(`/usuaris/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useSyncEntra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<ResultatSyncDto>('/usuaris/sync-entra', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usuaris'] }),
  });
}

export interface PersonaEntraDto {
  entraOid: string;
  upn?: string | null;
  nom?: string | null;
  cognoms?: string | null;
  email?: string | null;
  carrec?: string | null;
  departament?: string | null;
  usuariId?: number | null;
}

export interface AltaUsuariDto {
  entraOid: string;
  rolId: number;
  ambit: Ambit;
  ambitId?: number | null;
}

export function useCercaEntra(text: string) {
  const net = text.trim();
  return useQuery({
    queryKey: ['usuaris', 'entra', net],
    queryFn: () => apiRequest<PersonaEntraDto[]>(`/usuaris/entra${query({ text: net })}`),
    enabled: net.length >= 2,
    staleTime: 30_000,
  });
}

export function useAltaUsuari() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cos: AltaUsuariDto) =>
      apiRequest<UsuariDetallDto>('/usuaris', { method: 'POST', json: cos }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usuaris'] }),
  });
}

export function useAssignaRol(usuariId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: UsuariRolDto) =>
      apiRequest<UsuariRolDto>(`/usuaris/${usuariId}/rols`, { method: 'POST', json: r }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usuaris', 'detall', usuariId] }),
  });
}

export function useTreuRol(usuariId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignacioId: number) =>
      apiRequest<void>(`/usuaris/${usuariId}/rols/${assignacioId}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usuaris', 'detall', usuariId] }),
  });
}

export function useRols() {
  return useLlista<RolDto>(['rols'], '/rols');
}

export function useCatalegPermisos() {
  return useQuery({
    queryKey: ['permisos'],
    queryFn: () => apiRequest<PermisDto[]>('/permisos'),
    staleTime: 300_000,
  });
}

export function usePermisosRol(id: number | null) {
  return useQuery({
    queryKey: ['rols', id, 'permisos'],
    queryFn: () => apiRequest<PermisDto[]>(`/rols/${id}/permisos`),
    enabled: id != null,
  });
}

export function useDesaPermisosRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permisos }: { id: number; permisos: PermisDto[] }) =>
      apiRequest<PermisDto[]>(`/rols/${id}/permisos`, { method: 'PUT', json: permisos }),
    onSuccess: (_r, v) => void qc.invalidateQueries({ queryKey: ['rols', v.id, 'permisos'] }),
  });
}

export function useServeisAdmin() {
  return useLlista<ServeiAdminDto>(['serveis'], '/serveis');
}

export function useAreesAdmin() {
  return useLlista<AreaDto & { descripcio?: string | null; actiu?: boolean }>(['arees'], '/arees');
}

export function useDepartamentsAdmin() {
  return useLlista<DepartamentDto>(['departaments'], '/departaments');
}

export function useZones() {
  return useLlista<ZonaAdminDto>(['zones', 'admin'], '/zones');
}

export function useUsuarisInformar(serveiId: number | null) {
  return useQuery({
    queryKey: ['serveis', serveiId, 'usuaris-informar'],
    queryFn: () =>
      apiRequest<UsuariInformarDto[]>(`/serveis/${serveiId}/usuaris-informar`).catch(
        senseAcces<UsuariInformarDto[]>([]),
      ),
    enabled: serveiId != null,
  });
}

/** Llistes d'usuaris a informar de diversos serveis (comparteix la memòria cau de useUsuarisInformar). */
export function useUsuarisInformarServeis(serveiIds: number[]) {
  return useQueries({
    queries: serveiIds.map((id) => ({
      queryKey: ['serveis', id, 'usuaris-informar'],
      queryFn: () =>
        apiRequest<UsuariInformarDto[]>(`/serveis/${id}/usuaris-informar`).catch(
          senseAcces<UsuariInformarDto[]>([]),
        ),
    })),
  });
}

export function useAltaUsuariInformar(serveiId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: UsuariInformarDto) =>
      apiRequest<UsuariInformarDto>(`/serveis/${serveiId}/usuaris-informar`, {
        method: 'POST',
        json: u,
      }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['serveis', serveiId, 'usuaris-informar'] }),
  });
}

export function useBaixaUsuariInformar(serveiId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/serveis/${serveiId}/usuaris-informar/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['serveis', serveiId, 'usuaris-informar'] }),
  });
}

export function useImportaExcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      serveiId,
      fitxer,
      mapeig,
      validar,
    }: {
      serveiId: number;
      fitxer: File;
      mapeig: MapeigDto;
      validar: boolean;
    }) => {
      const form = new FormData();
      form.append('fitxer', fitxer, fitxer.name);
      form.append('mapeig', JSON.stringify(mapeig));
      const ruta = `/serveis/${serveiId}/usuaris-informar/import-excel${validar ? '/validar' : ''}`;
      return apiRequest<ImportResultatDto>(ruta, { method: 'POST', body: form });
    },
    onSuccess: (_r, v) => {
      if (!v.validar)
        void qc.invalidateQueries({ queryKey: ['serveis', v.serveiId, 'usuaris-informar'] });
    },
  });
}

export interface CircuitAdminDto {
  id?: number | null;
  nom: string;
  perDefecte: boolean;
  /** Inactiu: no s'ofereix per assignar a serveis nous. */
  actiu?: boolean;
  serveiId?: number | null;
  tipusAvisId?: number | null;
  nivells: NivellEscalatDto[];
  version?: number;
}

export function useCircuits() {
  return useLlista<CircuitAdminDto>(['circuits'], '/circuits-escalat');
}

export function useDesaCircuit(etag?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: CircuitAdminDto) => desa('/circuits-escalat', c, etag),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['circuits'] });
      void qc.invalidateQueries({ queryKey: ['circuit'] });
      void qc.invalidateQueries({ queryKey: ['etag'] });
    },
  });
}

/** Tipus d'avís per administrar: inclou els inactius (clau pròpia, separada de la del formulari d'alta). */
export function useTipusAvisAdmin() {
  return useLlista<TipusAvisAdminDto>(['tipus-avis', 'admin'], '/tipus-avis?actius=false');
}

export function usePlantilles() {
  return useLlista<PlantillaDto>(['plantilles', 'admin'], '/plantilles?actives=false');
}

export function usePrevisualitzaPlantilla() {
  return useMutation({
    mutationFn: ({
      id,
      canal,
      idioma,
      variables,
    }: {
      id: number;
      canal: Canal;
      idioma: string;
      variables: Record<string, string>;
    }) =>
      apiRequest<PrevisualitzacioPlantillaDto>(
        `/plantilles/${id}/previsualitzar${query({ canal, idioma })}`,
        { method: 'POST', json: { variables } },
      ),
  });
}

export function useDocumentsServeis(serveiIds: number[]) {
  return useQuery({
    queryKey: ['documents', 'biblioteca', serveiIds],
    queryFn: async () => {
      const llistes = await Promise.all(
        serveiIds.map((id) =>
          apiRequest<DocumentAdminDto[]>(`/serveis/${id}/documents`).catch(
            senseAcces<DocumentAdminDto[]>([]),
          ),
        ),
      );
      return llistes.flat();
    },
    enabled: serveiIds.length > 0,
  });
}

export function useDocument(id: number) {
  return useQuery({
    queryKey: ['documents', id],
    queryFn: () => apiRequest<DocumentAdminDto>(`/documents/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useVersionsDocument(id: number) {
  return useQuery({
    queryKey: ['documents', id, 'versions'],
    queryFn: () => apiRequest<DocumentVersioDto[]>(`/documents/${id}/versions`),
    enabled: Number.isFinite(id),
  });
}

export function usePujaDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      serveiId,
      documentId,
      fitxer,
      titol,
      categoria,
      ambit,
      comentari,
    }: {
      serveiId: number;
      documentId?: number | null;
      fitxer: File;
      titol?: string;
      categoria?: string;
      ambit?: AmbitDocument;
      comentari?: string;
    }) => {
      const form = new FormData();
      form.append('fitxer', fitxer, fitxer.name);
      if (comentari) form.append('comentari', comentari);
      if (documentId != null) {
        return apiRequest<DocumentAdminDto>(`/documents/${documentId}/substituir`, {
          method: 'POST',
          body: form,
        });
      }
      form.append('titol', titol ?? fitxer.name);
      if (categoria) form.append('categoria', categoria);
      form.append('ambit', ambit ?? 'SERVEI');
      return apiRequest<DocumentAdminDto>(`/serveis/${serveiId}/documents`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useRetiraDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motiu }: { id: number; motiu: string }) =>
      apiRequest<DocumentAdminDto>(`/documents/${id}/retirar`, { method: 'POST', json: { motiu } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

/** Política de fitxers amb el seu ETag (data = {data, etag}). */
export function usePoliticaFitxers() {
  return useQuery({
    queryKey: ['politica-fitxers'],
    queryFn: () => apiRequestAmbEtag<PoliticaFitxersDto>('/documents/politica'),
    refetchOnWindowFocus: false,
  });
}

export function useDesaPoliticaFitxers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ politica, etag }: { politica: PoliticaFitxersDto; etag: string | null }) =>
      desaRecurs('/documents/politica', politica, etag),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['politica-fitxers'] }),
  });
}

export function useAppVersions() {
  return useLlista<AppVersioDto>(['app', 'versions'], '/app/versions');
}

export function useMissatgesAdmin() {
  return useLlista<MissatgeAdminDto>(['app', 'missatges'], '/app/missatges');
}

/** Configuració general amb el seu ETag (data = {data, etag}). */
export function useConfiguracio() {
  return useQuery({
    queryKey: ['configuracio'],
    queryFn: () => apiRequestAmbEtag<ConfiguracioDto>('/configuracio'),
    refetchOnWindowFocus: false,
  });
}

export function useDesaConfiguracio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ config, etag }: { config: ConfiguracioDto; etag: string | null }) =>
      desaRecurs('/configuracio', config, etag),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['configuracio'] });
      void qc.invalidateQueries({ queryKey: ['auditoria'] });
    },
  });
}

export function useAuditoria(filtre: {
  entitat?: string;
  entitatId?: string | number;
  accio?: string;
  page?: number;
  size?: number;
}) {
  return useQuery({
    queryKey: ['auditoria', filtre],
    queryFn: () =>
      apiRequest<Pagina<RegistreAuditoriaDto>>(
        `/auditoria${query({ ...filtre, page: filtre.page ?? 0, size: filtre.size ?? 20 })}`,
      ).catch(senseAcces<Pagina<RegistreAuditoriaDto>>({ items: [], page: 0, size: 20, total: 0 })),
  });
}

export function useMonitoratge() {
  return useQuery({
    queryKey: ['monitoratge', 'enviaments'],
    queryFn: () => apiRequest<EstatMonitoratge>('/monitoratge/enviaments'),
    retry: false,
  });
}
